/**
 * Structured incident reports for Images Bulk Upload support pastes
 * @module services/images/bulk-upload-incident
 */

import isFinite from 'lodash/isFinite'

import { addPageAction, noticeError } from '../newrelic'

export const BULK_UPLOAD_FEATURE = 'images-bulk-upload'

export const BULK_UPLOAD_STAGE_FILE_READ = 'file-read'
export const BULK_UPLOAD_STAGE_CONTENT_LOOKUP = 'content-lookup'
export const BULK_UPLOAD_STAGE_CREATE = 'create'

export const BULK_UPLOAD_EVENT_IMAGE_FAILED = 'BulkUploadImageFailed'
export const BULK_UPLOAD_EVENT_FILE_READ_RECOVERED = 'BulkUploadFileReadRecovered'
export const BULK_UPLOAD_EVENT_BATCH_SUMMARY = 'BulkUploadBatchSummary'

const toCount = (value) => (isFinite(value) ? value : null)

/**
 * Create a batch id shared across one drop or publish run
 * @returns {String}
 */
export function createBulkUploadBatchId () {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Build a structured incident report for a single failed image
 * @param {Object} params
 * @returns {Object}
 */
export function createBulkUploadIncidentReport ({
  batchId,
  fileName,
  itemId,
  stage,
  urlPresent,
  urlLength,
  httpStatus,
  errorName,
  errorCode,
  fileSize,
  fileType,
  brooklynDetail,
  message,
  rawMessage,
  batchSize,
  attemptsMade = 1,
  elapsedMs,
} = {}) {
  const attempts = isFinite(attemptsMade) && attemptsMade > 0 ? attemptsMade : 1

  return {
    feature: BULK_UPLOAD_FEATURE,
    batchId: batchId || null,
    fileName: fileName || null,
    itemId: itemId || null,
    stage: stage || null,
    urlPresent: Boolean(urlPresent),
    urlLength: isFinite(urlLength) ? urlLength : 0,
    httpStatus: httpStatus == null ? null : httpStatus,
    errorName: errorName || null,
    // ECONNABORTED marks our own 30s timeout, so it stays separate from an
    // abort the browser or network caused
    errorCode: errorCode || null,
    fileSize: toCount(fileSize),
    fileType: fileType || null,
    brooklynDetail: brooklynDetail || null,
    message: message || null,
    rawMessage: rawMessage || null,
    batchSize: toCount(batchSize),
    attemptsMade: attempts,
    retriesApplied: attempts > 1,
    elapsedMs: toCount(elapsedMs),
    time: new Date().toISOString(),
  }
}

/**
 * Build a report when file-read retry succeeds after an earlier failure
 * @param {Object} params
 * @returns {Object}
 */
export function createBulkUploadFileReadRecoveredReport ({
  batchId,
  fileName,
  itemId,
  batchSize,
  attemptsMade,
  fileSize,
  fileType,
  elapsedMs,
} = {}) {
  return {
    feature: BULK_UPLOAD_FEATURE,
    batchId: batchId || null,
    fileName: fileName || null,
    itemId: itemId || null,
    stage: BULK_UPLOAD_STAGE_FILE_READ,
    fileSize: toCount(fileSize),
    fileType: fileType || null,
    batchSize: toCount(batchSize),
    attemptsMade: toCount(attemptsMade),
    elapsedMs: toCount(elapsedMs),
    time: new Date().toISOString(),
  }
}

/**
 * Build a summary of one batch so lost images are visible as a count gap
 * between queued and attempted
 * @param {Object} params
 * @returns {Object}
 */
export function createBulkUploadBatchSummary ({
  batchId,
  queued,
  attempted,
  succeeded,
  failed,
  skipped,
  elapsedMs,
} = {}) {
  return {
    feature: BULK_UPLOAD_FEATURE,
    batchId: batchId || null,
    queued: toCount(queued),
    attempted: toCount(attempted),
    succeeded: toCount(succeeded),
    failed: toCount(failed),
    skipped: toCount(skipped),
    elapsedMs: toCount(elapsedMs),
    time: new Date().toISOString(),
  }
}

/**
 * Format an incident report as pasteable text for Admin App support
 * @param {Object} report
 * @returns {String}
 */
export function formatBulkUploadIncidentReportForSupport (report = {}) {
  const lines = [
    `--- Friendly Admin App Support / ${report.feature || BULK_UPLOAD_FEATURE} ---`,
    `batchId: ${report.batchId ?? ''}`,
    `file: ${report.fileName ?? ''}`,
    `itemId: ${report.itemId ?? ''}`,
    `stage: ${report.stage ?? ''}`,
    `urlPresent: ${report.urlPresent ? 'true' : 'false'}`,
    `urlLength: ${report.urlLength ?? 0}`,
    `httpStatus: ${report.httpStatus == null ? 'no response' : report.httpStatus}`,
    `errorName: ${report.errorName ?? ''}`,
    `errorCode: ${report.errorCode ?? ''}`,
    `fileSize: ${report.fileSize == null ? '' : report.fileSize}`,
    `fileType: ${report.fileType ?? ''}`,
    `brooklynDetail: ${report.brooklynDetail ?? ''}`,
    `message: ${report.message ?? ''}`,
    `rawMessage: ${report.rawMessage ?? ''}`,
    `batchSize: ${report.batchSize == null ? '' : report.batchSize}`,
    `attemptsMade: ${report.attemptsMade ?? 1}`,
    `retriesApplied: ${report.retriesApplied ? 'true' : 'false'}`,
    `elapsedMs: ${report.elapsedMs == null ? '' : report.elapsedMs}`,
    `time: ${report.time ?? ''}`,
    '---',
  ]
  return lines.join('\n')
}

/**
 * Format every failure in a batch as one pasteable block for support
 * @param {Object} params
 * @param {Object} [params.summary] The batch summary
 * @param {Array} [params.reports] The failed image reports
 * @returns {String}
 */
export function formatBulkUploadSupportPaste ({ summary, reports = [] } = {}) {
  const blocks = reports.map(formatBulkUploadIncidentReportForSupport)
  if (!summary) {
    return blocks.join('\n')
  }

  const header = [
    `--- Friendly Admin App Support / ${summary.feature || BULK_UPLOAD_FEATURE} batch ---`,
    `batchId: ${summary.batchId ?? ''}`,
    `queued: ${summary.queued == null ? '' : summary.queued}`,
    `attempted: ${summary.attempted == null ? '' : summary.attempted}`,
    `succeeded: ${summary.succeeded == null ? '' : summary.succeeded}`,
    `failed: ${summary.failed == null ? '' : summary.failed}`,
    `skipped: ${summary.skipped == null ? '' : summary.skipped}`,
    `elapsedMs: ${summary.elapsedMs == null ? '' : summary.elapsedMs}`,
    `time: ${summary.time ?? ''}`,
    '---',
  ].join('\n')

  return [header, ...blocks].join('\n')
}

/**
 * Log a report for DevTools users and return the formatted paste string
 * @param {Object} report
 * @returns {String}
 */
export function logBulkUploadIncident (report) {
  const formatted = formatBulkUploadIncidentReportForSupport(report)
  // eslint-disable-next-line no-console
  console.error(report, formatted)
  return formatted
}

/**
 * Send a failed image to New Relic so failures are queryable without waiting
 * for a user to paste a report
 * @param {Object} report The incident report
 * @param {Error} [error] The originating error, recorded with the report fields
 */
export function emitBulkUploadIncident (report, error) {
  addPageAction(BULK_UPLOAD_EVENT_IMAGE_FAILED, report)
  if (error) {
    noticeError(error, report)
  }
}

/**
 * Record a file-read retry that succeeded so near-misses are measurable in NR
 * @param {Object} report
 */
export function emitBulkUploadFileReadRecovered (report) {
  addPageAction(BULK_UPLOAD_EVENT_FILE_READ_RECOVERED, report)
}

/**
 * Send the outcome of a whole batch to New Relic
 * @param {Object} summary The batch summary
 */
export function emitBulkUploadBatchSummary (summary) {
  addPageAction(BULK_UPLOAD_EVENT_BATCH_SUMMARY, summary)
}
